import React, { useState, useEffect } from "react";
import { FaCheckCircle, FaExclamationCircle, FaClipboardList } from "react-icons/fa";
import api from "../../services/api";
import "../admin/admin.css"; // reuse same styles
import { useNotifications } from "../../context/NotificationContext";

const MyTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Submit modal
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [file, setFile] = useState(null);
  const [comment, setComment] = useState("");

  const getToken = () => localStorage.getItem("token");

  const { resetTasks } = useNotifications();

  useEffect(() => {
    resetTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      setIsLoading(true);
      const token = getToken();
      const res = await api.get("/user/tasks", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTasks(res.data.tasks || []);
      setErrorMsg("");
    } catch (err) {
      if (err.response?.status === 401) {
        setErrorMsg("Session expired. Please log in again.");
      } else {
        setErrorMsg(err.response?.data?.message || "Failed to load tasks");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const openSubmitModal = (task) => {
    setSelectedTask(task);
    setFile(null);
    setComment("");
    setShowSubmitModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTask) return;
    try {
      const formData = new FormData();
      if (file) formData.append("file", file);
      formData.append("comment", comment);

      await api.post(`/user/tasks/${selectedTask.id}/submit`, formData, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setSuccessMsg("Task submitted successfully!");
      setShowSubmitModal(false);
      fetchTasks();
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "Submission failed");
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  return (
    <div className="admin-container">
      <div className="header-banner mb-4">
        <div className="header-content">
          <div>
            <h1 className="header-title">My Tasks </h1>
            <p className="header-subtitle">View and submit your assigned tasks</p>
          </div>
        </div>
      </div>

      {successMsg && <div className="success-message"><FaCheckCircle style={{ marginRight: "6px" }} />{successMsg}</div>}
      {errorMsg && <div className="error-message"><FaExclamationCircle style={{ marginRight: "6px" }} />{errorMsg}</div>}

      <div className="students-card">
        <div className="card-header-custom">
          <div>
            <h3 className="card-title">Assigned Tasks</h3>
            <p className="card-subtitle">{tasks.length} tasks</p>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="students-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Description</th>
                <th>Status</th>
                <th>Due Date</th>
                <th>File</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    <div className="loading-spinner"></div>
                    <p className="mt-2">Loading tasks...</p>
                  </td>
                </tr>
              ) : tasks.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty-state">
                    <div className="empty-state-content">
                      <div className="empty-icon"><FaClipboardList /></div>
                      <p className="empty-text">No tasks assigned yet</p>
                      <p className="empty-subtext">Tasks assigned by admin will appear here</p>
                    </div>
                  </td>
                </tr>
              ) : (
                tasks.map((task) => (
                  <tr key={task.id}>
                    <td>
                      <strong>{task.title}</strong>
                    </td>
                    <td>{task.description || "—"}</td>
                    <td>
                      <span className={`status-badge status-${task.status}`}>
                        {task.status}
                      </span>
                    </td>
                    <td>{task.due_date || "—"}</td>
                    <td>
                      {task.file_path ? (
                        <a
                          href={`https://dt-attendance-system.desired-techs.com/dt-backend/${task.file_path}`}
                          target="_blank"
                          rel="noreferrer"
                          className="btn-action btn-view"
                          download
                        >
                          Download
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {task.status === 'pending' && (
                        <button
                          className="btn-action btn-checkin"
                          onClick={() => openSubmitModal(task)}
                        >
                          Submit Work
                        </button>
                      )}
                      {task.status === 'submitted' && (
                        <span className="text-warning">Under review</span>
                      )}
                      {task.status === 'approved' && (
                        <span className="text-success"><FaCheckCircle style={{ marginRight: "6px" }} />Approved</span>
                      )}
                      {task.status === 'rejected' && (
                        <span className="text-danger"><FaExclamationCircle style={{ marginRight: "6px" }} />Rejected</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* SUBMIT MODAL */}
      {showSubmitModal && selectedTask && (
        <div className="modal-overlay" onClick={() => setShowSubmitModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <div>
                <h3 className="modal-title">Submit Task: {selectedTask.title}</h3>
                <p className="modal-subtitle">Upload your completed work</p>
              </div>
              <button className="modal-close" onClick={() => setShowSubmitModal(false)} aria-label="Close submit task modal"><FaTimes /></button>
            </div>
            <div className="modal-body-custom">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">Attach File (optional)</label>
                  <input
                    type="file"
                    className="form-input"
                    onChange={(e) => setFile(e.target.files[0])}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Comments</label>
                  <textarea
                    className="form-input"
                    style={{resize: 'none'}}
                    rows="3"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Any notes about the submission..."
                  ></textarea>
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => setShowSubmitModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-submit">
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTasks;